import * as z from "zod";
import { Phone } from "lucide-react";
import type { CommunicationsInsertInput } from "@/src/gql/graphql";

export const communicationSchema = z.object({
  title: z.string().min(1, "Title is required."),
  type_id: z.string().uuid("Please select a communication type."),
  body: z.string().min(1, "Communication details cannot be empty."),
  direction: z.enum(["inbound", "outbound"]).optional(),
  status: z.string().optional(),
  external_id: z.string().optional(),
  occurred_at: z.date().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
  // When parent does not pass a contactId, this form requires selecting one
  contact_id: z.string().uuid().optional(),
  // Follow-up task fields
  follow_up_schedule: z.boolean().default(false),
  follow_up_due_date: z.date().optional(),
  follow_up_note: z.string().optional(),
});

export type CommunicationFormValues = z.infer<typeof communicationSchema>;

export type CommunicationInsertFields = Pick<
  CommunicationsInsertInput,
  "title" | "type_id" | "body" | "direction" | "status" | "external_id" | "occurred_at" | "metadata"
>;

export type CommunicationFormSubmission = CommunicationInsertFields & {
  contact_id: string;
  user_id: string;
  follow_up_schedule: boolean;
  follow_up_due_date?: Date;
  follow_up_note?: string;
};

export interface CommunicationFormProps {
  contactId?: string;
  userId: string;
  onSubmit: (values: CommunicationFormSubmission) => Promise<void>;
  isSubmitting: boolean;
  typeOptions?: Array<{ value: string; label: string }>;
  isLoadingTypes?: boolean;
}

export const COMMUNICATION_DIRECTION_OPTIONS = [
  { value: "inbound", label: "Inbound", icon: Phone, description: "Received from contact" },
  { value: "outbound", label: "Outbound", icon: Phone, description: "Sent to contact" },
];

export const COMMUNICATION_STATUS_OPTIONS = [
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "scheduled", label: "Scheduled" },
  { value: "cancelled", label: "Cancelled" },
  { value: "follow_up_needed", label: "Follow-up Needed" },
];

// Pre-derived select options (icon stripped) — use these in components directly
export const COMMUNICATION_DIRECTION_SELECT_OPTIONS = COMMUNICATION_DIRECTION_OPTIONS.map(
  ({ value, label, description }) => ({ value, label, description })
);

export const COMMUNICATION_STATUS_SELECT_OPTIONS = COMMUNICATION_STATUS_OPTIONS.map(
  ({ value, label }) => ({ value, label })
);
